/**
 * Indian-scale money formatting for WMS.
 *
 * WHY THIS EXISTS
 *   Every screen in the module rolled its own scaling, and each one fixed the unit in
 *   advance: the tender execution profile divided by 1e7 and printed "Cr", the tender
 *   pipeline divided by 1e5 and printed "L". The same ₹40,000 tender therefore read as
 *   "₹0.40 L" on one screen and "₹0.00 Cr" on the next — and anything under ₹50,000
 *   rounded to zero outright, so a ₹40,000 estimate, an ₹800 EMD and a ₹0.05
 *   qualification threshold all displayed as nothing at all.
 *
 *   The values were stored correctly throughout. The formatter was discarding them.
 *
 * THE RULE
 *   DTCP works at every scale — a ward drain is lakhs, a trunk main is crores — so the
 *   unit follows the value instead of being decided by the column heading.
 *
 * WHEN NOT TO USE fmtMoney
 *   A chart axis, or a column of figures meant to be compared down the page, needs ONE
 *   unit for the whole set or the numbers stop being comparable. Use fmtMoneyIn() with
 *   an explicit unit for those, and put the unit in the axis or header label.
 */

/** Formats a number with Indian digit grouping. */
const grouped = (n: number, min: number, max: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: min, maximumFractionDigits: max });

/**
 * Money at whatever scale the value actually is.
 *
 *      0        -> ₹0
 *      0.05     -> ₹0.05
 *    800        -> ₹800
 *  40000        -> ₹40,000
 * 100000        -> ₹1.00 L
 * 10000000      -> ₹1.00 Cr
 */
export const fmtMoney = (v: number | string | null | undefined): string => `₹${fmtAmount(v)}`;

/**
 * Same scaling as fmtMoney, without the ₹ symbol — for the many call sites that
 * already write their own, e.g. `₹{fmtCurrency(row.grss_at)}`.
 *
 *      0        -> 0
 *      0.05     -> 0.05
 *    800        -> 800
 *  40000        -> 40,000
 * 100000        -> 1.00 L
 * 10000000      -> 1.00 Cr
 */
export const fmtAmount = (v: number | string | null | undefined): string => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${grouped(n / 1e7, 2, 2)} Cr`;
  if (abs >= 1e5) return `${grouped(n / 1e5, 2, 2)} L`;
  // Plain rupees below a lakh, to two decimals but without trailing zeros, so
  // 40000 reads "40,000" and 0.05 survives as "0.05" rather than rounding away.
  return grouped(n, 0, 2);
};

/**
 * Money forced to one unit, for columns and axes that must stay comparable.
 * The caller is responsible for stating the unit in the heading.
 *
 * fmtMoneyIn(40000, 'L')  -> "0.40"
 * fmtMoneyIn(40000, 'Cr') -> "0.004"   (3 decimals, so small values do not vanish)
 */
export const fmtMoneyIn = (v: number | string | null | undefined, unit: 'Cr' | 'L' | '₹'): string => {
  const n = Number(v) || 0;
  if (unit === 'Cr') return grouped(n / 1e7, 2, 3);
  if (unit === 'L') return grouped(n / 1e5, 2, 2);
  return grouped(n, 0, 2);
};

/** Same as fmtMoney, but an empty value renders as a dash rather than ₹0. */
export const fmtMoneyOrDash = (v: number | string | null | undefined): string =>
  (Number(v) || 0) === 0 ? '—' : fmtMoney(v);
